import { PlanFamily, TaskSkill } from './api';
import { Icon } from '@phosphor-icons/react';
import { IconName } from 'src/components/Icon';
import { PriceDiscountType } from './wallet/models/PriceDiscountType';

export type PlanSectionType =
  | 'Standard'
  | 'Pro'
  | 'Ultra'
  | 'Teams_Standard'
  | 'Teams_Pro'
  | 'Teams_Ultra'
  | 'SuperNinja_Ultra'
  | 'SuperNinja_Teams_Ultra'
  | 'Teams_SuperNinja_Ultra'
  | 'SuperNinja_Ultra_10'
  | 'SuperNinja_Ultra_20'
  | 'Teams_SuperNinja_Ultra_10'
  | 'Teams_SuperNinja_Ultra_20';

export interface PaymentPlanOption {
  lookup_key: string;
  amount: number;
  isDefault?: boolean;
  tasksCount?: number;
  isAnnual?: boolean;
  promotion_code?: string;
}

export interface PaymentPlanOptionWithSale extends PaymentPlanOption {
  newAmount: number;
  sale: number;
  couponAmount: number;
}

export interface PaymentsActivity {
  skill: TaskSkill;
  task_hash: string;
  timestamp: string;
  cost: number;
  status: string;
  models: PaymentsActivityModel[];
  components: PaymentsActivityComponent[];
}

export interface PaymentsActivityModel {
  platform: string;
  provider: string;
  model_name: string;
}

export interface PaymentsActivityComponent {
  component: string;
}

export interface PaymentsActivityColumn {
  header: string;
  accessor: keyof PaymentsActivity;
  render?: (activity: PaymentsActivity) => React.ReactNode;
  format?: (value: number | string) => string;
}

/**
 * @deprecated
 */
export enum TeamsTabs {
  INDIVIDUAL = 'individual',
  BUSINESS = 'business',
}

export enum PaymentTabs {
  INDIVIDUAL = 'individual',
  BUSINESS = 'business',
}

export interface PlanOption {
  title: string;
  withBorder?: boolean;
  link?: { href: string; label: string };
  isExternalModels?: boolean;
}

export type WelcomeUpsellModalType =
  | 'free-tier-welcome-modal'
  | 'standard-welcome-modal'
  | 'pro-welcome-modal'
  | 'ultra-welcome-modal'
  | 'max-welcome-modal'
  | 'enterprise-tier-welcome-modal'
  | 'teams-standard-welcome-modal'
  | 'teams-pro-welcome-modal'
  | 'teams-ultra-welcome-modal'
  | 'ultra-trial-welcome-modal'
  | 'max-trial-welcome-modal'
  | 'standard-trial-welcome-modal'
  | 'teams-max-welcome-modal';

export type WelcomeUpsellModalTypeV2 =
  | 'free-tier-welcome-modal'
  | 'pro-10-welcome-modal'
  | 'pro-30-welcome-modal'
  | 'pro-trial-welcome-modal'
  | 'max-10-welcome-modal'
  | 'max-20-welcome-modal'
  | 'max-30-welcome-modal'
  | 'max-trial-welcome-modal'
  | 'business-10-welcome-modal'
  | 'business-20-welcome-modal'
  | 'business-30-welcome-modal'
  | 'max-05-welcome-modal';

export interface WelcomeUpsellData {
  title: string;
  description: string;
  continueButtonTitle: string;
  upgradeButtonTitle: string;
  isContinueButtonBlue?: boolean;
  upgradeButtonId: string;
  isUpgradeButtonBlue?: boolean;
  reverseButtonsOrder?: boolean;
  duration: number;
}

export interface WelcomeUpsellModalData {
  title: string;
  description: string;
  upgradeButtonId: string;
  features: {
    title: string;
    description: string | React.ReactNode;
    icon: React.ReactNode;
  }[];
  isMax?: boolean;
  couldUpdate?: boolean;
}

export type WelcomeUpsellContent = Record<
  WelcomeUpsellModalType,
  WelcomeUpsellData
>;

export type WelcomeUpsellModalContent = Record<
  WelcomeUpsellModalType,
  WelcomeUpsellModalData
>;

export type WelcomeUpsellModalContentV2 = Record<
  WelcomeUpsellModalTypeV2,
  WelcomeUpsellModalData
>;

export interface CreateSubscriptionRequest {
  user_id: string;
  plan_id: string;
  seat_count?: number | null;
  stripe_promotion_code?: string;
  referral_id?: string;
  discount_type?: PriceDiscountType;
}

export type TeamGroup = 'individual' | 'teams';

export interface GetSubscriptionPlansRequest {
  user_id: string;
  group?: TeamGroup;
  seat_count?: number | null;
  referral_id?: string;
  families?: PlanFamily[];
  discount_type?: PriceDiscountType;
}

export interface GetSubscriptionPlanRequest {
  user_id: string;
  plan_id: string;
  seat_count?: number | null;
  stripe_promotion_code?: string;
  referral_id?: string;
  discount_type?: PriceDiscountType;
}

export type SubscriptionIntentFlowType = 'payment_intent' | 'setup_intent';

export interface SubscriptionIntent {
  subscription_id: string;
  client_secret: string;
  flow: SubscriptionIntentFlowType;
}

export interface UpgradeDowngradeSubscriptionRequest {
  user_id: string;
  product_key: string;
  price_timestamp: number;
  seat_count?: number | null;
}

export interface SubscriptionPlanMetadata {
  paypal: {
    plan_id: string;
  };

  stripe: {
    lookup_key: string;
    price_id: string;
    coupon_metadata?: {
      coupon_id: string;
      promotion_code: string;
      promotion_code_id: string;
      amount_cents: 0;
    };
  };

  amp: {
    amp_product_link: string;
  };

  [key: string]: string | unknown;
}

export interface CreatePaypalSubscriptionRequest {
  subscription_id: string;
  user_id: string;
}

export enum PaymentPageRightSidePanelType {
  MonthlyToAnnually = 'MonthlyToAnnually',
  ChangeSeats = 'ChangeSeats',
}

export enum ManageCreditAccountRightSidePanelType {
  ManageCredits = 'ManageCredits',
  UpdatePaymentMethod = 'UpdatePaymentMethod',
}

export type PlanFeatureItemDeprecated = {
  id: string;
  icon: React.ReactNode;
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  isKeyFeature?: boolean;
};

export type IconVariant =
  | { Icon: Icon; iconName?: never }
  | { iconName: IconName; Icon?: never };

export type PlanModelItem = {
  title: string | React.ReactNode;
} & IconVariant;

export type PlanFeatureItem = {
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  modelList?: PlanModelItem[];
  isKeyFeature?: boolean;
  hideAndMoreModels?: boolean;
} & IconVariant;

export type PlanUsageFeature = {
  requestCount: number | string;
  concurrentTasksCount: number;
  deepResearchType: string;
  minMarketValue: number;
  maxMarketValue: number;
};

export type ActivePlanFamily = Exclude<
  PlanFamily,
  PlanFamily.UNDEFINED | PlanFamily.CREDIT
>;
